/* Custom element: <video-inline>
   Turns a muted looping background video into a normal, controllable one
   the first time the visitor presses play: sound on, native controls,
   no loop. After that the video still pauses when it scrolls out of view
   and resumes when it comes back, unless the visitor paused it by hand. */

(() => {
  'use strict';

  if (customElements.get('video-inline')) return;

  class VideoInline extends HTMLElement {
    #listeners = null;
    #triggerListener = null;
    #viewportObserver = null;
    #trigger = null;
    #video = null;
    #userPaused = false;
    // Set right before a programmatic pause so the scroll-away pause isn't
    // mistaken for the visitor pausing. The `pause` event is dispatched
    // asynchronously, so a flag is needed — detaching and reattaching the
    // listener around `pause()` would not suppress anything.
    #suppressNextPause = false;

    connectedCallback() {
      this.#trigger = this.querySelector('[data-video-inline-play]');
      if (!this.#trigger) return;

      this.#userPaused = false;
      this.#video = null;

      this.#triggerListener = new AbortController();
      this.#trigger.addEventListener('click', this.#onClick, { signal: this.#triggerListener.signal });
    }

    disconnectedCallback() {
      this.#triggerListener?.abort();
      this.#listeners?.abort();
      this.#viewportObserver?.disconnect();
      this.#triggerListener = null;
      this.#listeners = null;
      this.#viewportObserver = null;
    }

    #onClick = (event) => {
      event.preventDefault();

      const video = this.querySelector('video');
      if (!video) return;

      this.#video = video;
      video.muted = false;
      video.controls = true;
      video.loop = false;
      this.classList.add('is-playing');

      video.play().catch(() => {});

      // The trigger has done its job; don't let a second press re-run this.
      this.#triggerListener?.abort();
      this.#triggerListener = null;

      this.#takeControl(video);
    };

    /**
     * Hand playback over to the visitor: track manual pauses and keep the
     * video in step with the viewport.
     *
     * @param {HTMLVideoElement} video - the video the visitor just started.
     */
    #takeControl(video) {
      // Stop <video-background>'s scroll observer so it can't override the
      // visitor once they've taken control.
      const background = this.querySelector('video-background');
      background?.videoPlayObserver?.disconnect();

      this.#listeners = new AbortController();
      const { signal } = this.#listeners;

      video.addEventListener(
        'pause',
        () => {
          if (this.#suppressNextPause) {
            this.#suppressNextPause = false;
            return;
          }
          this.#userPaused = true;
        },
        { signal }
      );
      video.addEventListener(
        'play',
        () => {
          this.#userPaused = false;
        },
        { signal }
      );

      this.#viewportObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (!this.#userPaused && video.paused) {
              video.play().catch(() => {});
            }
            return;
          }

          if (!video.paused) {
            this.#suppressNextPause = true;
            video.pause();
          }
        },
        { threshold: 0 }
      );
      this.#viewportObserver.observe(video);
    }
  }

  customElements.define('video-inline', VideoInline);
})();
